import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { Can } from '../../auth/Can.js';
import type { AccountNode } from '../../types/bookkeeping.js';
import { bankingErrorMessage, flattenTree, fmtMoney } from './shared.js';

/** Converts a decimal string (e.g. "10.50") to a signed cents string, or null if invalid. */
function toCents(value: string): string | null {
  const raw = parseFloat(value) * 100;
  if (!Number.isFinite(raw)) return null;
  return String(Math.round(raw));
}

type ReconStatus = 'in_progress' | 'completed';

interface ReconSession {
  id: string;
  account_id: string;
  statement_date: string;
  statement_ending_balance: string;
  beginning_balance: string;
  status: ReconStatus;
  created_at: string;
  completed_at: string | null;
  row_count: number;
  checked_sum: string;
}

interface ReconRow {
  id: string;
  txn_date: string;
  description: string;
  amount: string;
  matched_journal_entry_id: string | null;
  checked: boolean;
}

interface ReconDetail {
  id: string;
  account_id: string;
  statement_date: string;
  statement_ending_balance: string;
  beginning_balance: string;
  status: ReconStatus;
  created_at: string;
  completed_at: string | null;
  rows: ReconRow[];
}

export function BankReconcilePage() {
  const { t } = useTranslation('bookkeeping');
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();

  const [accountId, setAccountId] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [statementDate, setStatementDate] = useState('');
  const [endingBalanceInput, setEndingBalanceInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });
  const allAccounts = flattenTree(accountsData?.tree ?? []);
  const accounts = allAccounts.filter((a) => a.type === 'asset' && a.is_active);

  const listQ = useQuery({
    queryKey: ['bk-recon-list', accountId],
    queryFn: () =>
      api<ReconSession[]>(`/bookkeeping/banking/reconciliations?account_id=${accountId}`),
    enabled: !!activeOrgId && !!accountId,
  });
  const sessions = listQ.data ?? [];
  const inProgress = sessions.find((s) => s.status === 'in_progress');
  const completed = sessions.filter((s) => s.status === 'completed');
  const latestCompleted = completed.reduce<ReconSession | null>((latest, s) => {
    if (!latest) return s;
    return (s.completed_at ?? '') > (latest.completed_at ?? '') ? s : latest;
  }, null);

  // Per-account capture-once: distinguishes a pre-existing in_progress session
  // (resumed) from one created during this view (fresh). Keyed on accountId so
  // switching accounts re-evaluates each account independently (spec READ-4).
  const wasOpenOnMountRef = useRef<Record<string, boolean>>({});
  useEffect(() => {
    if (
      accountId &&
      listQ.data !== undefined &&
      wasOpenOnMountRef.current[accountId] === undefined
    ) {
      wasOpenOnMountRef.current[accountId] = Boolean(
        listQ.data.find((s) => s.status === 'in_progress'),
      );
    }
  }, [listQ.data, accountId]);

  const effectiveSessionId = sessionId ?? inProgress?.id ?? null;

  const detailQ = useQuery({
    queryKey: ['bk-recon-detail', effectiveSessionId],
    queryFn: () => api<ReconDetail>(`/bookkeeping/banking/reconciliations/${effectiveSessionId}`),
    enabled: !!effectiveSessionId,
  });
  const detail = detailQ.data;

  const checkedSum = (detail?.rows ?? [])
    .filter((r) => r.checked)
    .reduce((acc, r) => acc + BigInt(r.amount), 0n);
  const beginning = detail ? BigInt(detail.beginning_balance) : 0n;
  const ending = detail ? BigInt(detail.statement_ending_balance) : 0n;
  const difference = beginning + checkedSum - ending;

  function selectAccount(id: string) {
    setAccountId(id);
    setSessionId(null);
    setStatementDate('');
    setEndingBalanceInput('');
    setError(null);
  }

  const startMut = useMutation({
    mutationFn: () => {
      const cents = toCents(endingBalanceInput);
      return api<ReconSession>('/bookkeeping/banking/reconciliations', {
        method: 'POST',
        body: JSON.stringify({
          account_id: accountId,
          statement_date: statementDate,
          statement_ending_balance: cents,
        }),
      });
    },
    onSuccess: async (res) => {
      setError(null);
      setSessionId(res.id);
      setStatementDate('');
      setEndingBalanceInput('');
      await qc.invalidateQueries({ queryKey: ['bk-recon-list', accountId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const toggleMut = useMutation({
    mutationFn: (bankTransactionId: string) =>
      api<{ checked: boolean }>(
        `/bookkeeping/banking/reconciliations/${effectiveSessionId}/toggle`,
        {
          method: 'POST',
          body: JSON.stringify({ bank_transaction_id: bankTransactionId }),
        },
      ),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['bk-recon-detail', effectiveSessionId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const finalizeMut = useMutation({
    mutationFn: () =>
      api<ReconSession>(`/bookkeeping/banking/reconciliations/${effectiveSessionId}/finalize`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      setError(null);
      setSessionId(null);
      await qc.invalidateQueries({ queryKey: ['bk-recon-list', accountId] });
      await qc.invalidateQueries({ queryKey: ['bk-recon-detail', effectiveSessionId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const cancelMut = useMutation({
    mutationFn: () =>
      api(`/bookkeeping/banking/reconciliations/${effectiveSessionId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setError(null);
      setSessionId(null);
      await qc.invalidateQueries({ queryKey: ['bk-recon-list', accountId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const undoMut = useMutation({
    mutationFn: (id: string) =>
      api(`/bookkeeping/banking/reconciliations/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['bk-recon-list', accountId] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const cents = toCents(endingBalanceInput);
  const canStart = statementDate.trim().length > 0 && cents !== null && !startMut.isPending;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('banking.recon_title')}</h1>

      <div className="rounded border bg-white p-4 text-sm">
        <label>
          {t('banking.account')}
          <select
            value={accountId}
            onChange={(e) => selectAccount(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          >
            <option value="">—</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} {a.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </p>
      )}

      {accountId && !effectiveSessionId && (
        <div className="grid grid-cols-3 gap-3 rounded border bg-white p-4 text-sm">
          <label>
            {t('banking.recon_statement_date')}
            <input
              type="date"
              value={statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
              className="mt-1 block w-full rounded border px-2 py-1"
            />
          </label>
          <label>
            {t('banking.recon_ending_balance')}
            <input
              type="number"
              step="0.01"
              value={endingBalanceInput}
              onChange={(e) => setEndingBalanceInput(e.target.value)}
              className="mt-1 block w-full rounded border px-2 py-1"
            />
          </label>
          <div className="flex items-end">
            <Can I="manage" a="Account">
              <button
                type="button"
                onClick={() => startMut.mutate()}
                disabled={!canStart}
                className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
              >
                {t('banking.recon_start')}
              </button>
            </Can>
          </div>
        </div>
      )}

      {accountId && effectiveSessionId && (
        <div className="space-y-3 rounded border bg-white p-4 text-sm">
          {inProgress && (
            <p className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
              {wasOpenOnMountRef.current[accountId]
                ? t('banking.recon_resumed')
                : t('banking.recon_fresh')}
            </p>
          )}

          <div className="grid grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-xs uppercase text-slate-500">
                {t('banking.recon_beginning_balance')}
              </div>
              <div className="font-mono">{fmtMoney(beginning.toString())}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-slate-500">
                {t('banking.recon_ending_balance')}
              </div>
              <div className="font-mono">{fmtMoney(ending.toString())}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-slate-500">
                {t('banking.recon_checked_sum')}
              </div>
              <div className="font-mono">{fmtMoney(checkedSum.toString())}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-slate-500">
                {t('banking.recon_difference')}
              </div>
              <div className={`font-mono ${difference === 0n ? 'text-green-600' : 'text-red-600'}`}>
                {fmtMoney(difference.toString())}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2" />
                  <th className="px-3 py-2">{t('banking.date')}</th>
                  <th className="px-3 py-2">{t('banking.description')}</th>
                  <th className="px-3 py-2 text-right">{t('banking.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {(detail?.rows.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-4 text-center text-slate-500">
                      {t('banking.recon_no_rows')}
                    </td>
                  </tr>
                )}
                {detail?.rows.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="px-3 py-2">
                      <Can I="manage" a="Account">
                        <input
                          type="checkbox"
                          checked={row.checked}
                          disabled={toggleMut.isPending}
                          onChange={() => toggleMut.mutate(row.id)}
                        />
                      </Can>
                    </td>
                    <td className="px-3 py-2">{row.txn_date}</td>
                    <td className="px-3 py-2">{row.description}</td>
                    <td
                      className={`px-3 py-2 text-right font-mono ${
                        row.amount.startsWith('-') ? 'text-red-600' : 'text-green-600'
                      }`}
                    >
                      {fmtMoney(row.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2">
            <Can I="manage" a="Account">
              <button
                type="button"
                onClick={() => finalizeMut.mutate()}
                disabled={difference !== 0n || finalizeMut.isPending || !detail}
                className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
              >
                {t('banking.recon_finalize')}
              </button>
            </Can>
            <Can I="manage" a="Account">
              <button
                type="button"
                onClick={() => cancelMut.mutate()}
                disabled={cancelMut.isPending}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                {t('banking.recon_cancel')}
              </button>
            </Can>
          </div>
        </div>
      )}

      {accountId && (
        <div>
          <h2 className="mb-2 text-sm font-semibold text-slate-600">
            {t('banking.recon_history')}
          </h2>
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t('banking.recon_statement_date')}</th>
                  <th className="px-3 py-2 text-right">{t('banking.recon_beginning_balance')}</th>
                  <th className="px-3 py-2 text-right">{t('banking.recon_ending_balance')}</th>
                  <th className="px-3 py-2 text-right">{t('banking.recon_checked_sum')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {completed.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-slate-500">
                      {t('banking.recon_no_history')}
                    </td>
                  </tr>
                )}
                {completed.map((s) => (
                  <tr key={s.id} className="border-t">
                    <td className="px-3 py-2">{s.statement_date}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {fmtMoney(s.beginning_balance)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {fmtMoney(s.statement_ending_balance)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(s.checked_sum)}</td>
                    <td className="px-3 py-2">
                      {latestCompleted?.id === s.id && (
                        <Can I="manage" a="Account">
                          <button
                            type="button"
                            onClick={() => undoMut.mutate(s.id)}
                            disabled={undoMut.isPending}
                            className="text-red-600 hover:underline"
                          >
                            {t('banking.recon_undo')}
                          </button>
                        </Can>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
